import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Skeleton,
    Snackbar,
    SnackbarContent,
    Typography,
} from '@mui/material';
import {
    AddShoppingCart as AddShoppingCartIcon,
    ArrowBack as ArrowBackIcon,
    VideoCall as VideoCallIcon,
} from '@mui/icons-material';

import { fetchStores } from '../../actions/storeActions';
import { fetchStoreProduct } from '../../actions/catalogActions';
import { addToCart } from '../../actions/cartActions';
import CartButton from '../cart/CartButton';
import CustomerLoginDialog from '../cart/CustomerLoginDialog';
import QuantityStepper from '../cart/QuantityStepper';
import { cartPath } from '../cart/cartPaths';
import PriceTag from './PriceTag';
import ProductImage from './ProductImage';
import { callRequestPath } from './StorePage';
import {
    COLORS,
    FONT_BODY,
    FONT_TITLE,
    FONTS_HREF,
    callButtonSx,
    secondaryButtonSx,
    skeletonSx,
} from './storefrontStyles';

const storePath = (storeId) => `/tienda/${storeId}`;

const Gallery = ({ images, name }) => {
    const [selected, setSelected] = useState(0);
    const list = images && images.length > 0 ? images : [null];
    const current = list[Math.min(selected, list.length - 1)];

    return (
        <Box>
            <ProductImage
                src={current && current.url}
                alt={name}
                sx={{
                    borderRadius: '12px',
                    border: `1px solid ${COLORS.border}`,
                }}
            />
            {list.length > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        gap: 1,
                        mt: 1.5,
                        overflowX: 'auto',
                        pb: 0.5,
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    {list.map((image, i) => (
                        <Box
                            key={image.productImageId}
                            component="button"
                            type="button"
                            aria-label={`Ver imagen ${i + 1}`}
                            onClick={() => setSelected(i)}
                            sx={{
                                flexShrink: 0,
                                width: 64,
                                p: 0,
                                borderRadius: '6px',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                background: 'none',
                                border: `2px solid ${
                                    i === selected
                                        ? COLORS.accent
                                        : 'transparent'
                                }`,
                                opacity: i === selected ? 1 : 0.7,
                                transition: 'opacity 0.2s, border-color 0.2s',
                                '&:hover': { opacity: 1 },
                            }}
                        >
                            <ProductImage
                                src={image.url}
                                alt={`${name} ${i + 1}`}
                            />
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};

// Un grupo de chips por tipo de opción. La selección va a la API como
// { [nombreDeLaOpción]: valor }, que es lo que valida helpers/cart.js.
export const ProductOptions = ({ options, selected, onSelect, disabled }) => {
    if (!options || options.length === 0) return null;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {options.map((option) => (
                <Box
                    key={option.productOptionTypeId}
                    role="radiogroup"
                    aria-label={option.name}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography
                        sx={{
                            color: COLORS.muted,
                            fontFamily: FONT_BODY,
                            fontSize: '0.9rem',
                            mr: 0.5,
                        }}
                    >
                        {option.name}:
                    </Typography>
                    {(option.values || []).map((value) => {
                        const isSelected =
                            selected[option.name] === value.value;
                        return (
                            <Chip
                                key={value.productOptionValueId}
                                label={value.value}
                                role="radio"
                                aria-label={`${option.name} ${value.value}`}
                                aria-checked={isSelected}
                                disabled={disabled}
                                onClick={() =>
                                    onSelect(option.name, value.value)
                                }
                                sx={{
                                    color: isSelected
                                        ? COLORS.background
                                        : 'white',
                                    backgroundColor: isSelected
                                        ? 'white'
                                        : 'rgba(255,255,255,0.06)',
                                    border: `1px solid ${
                                        isSelected ? 'white' : COLORS.border
                                    }`,
                                    fontFamily: FONT_BODY,
                                    fontWeight: isSelected ? 600 : 400,
                                    minWidth: 44,
                                    '&:hover': {
                                        backgroundColor: isSelected
                                            ? 'white'
                                            : 'rgba(255,255,255,0.14)',
                                    },
                                    '&.Mui-disabled': { opacity: 0.6 },
                                }}
                            />
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
};

const DetailSkeleton = () => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 3, md: 5 },
        }}
    >
        <Skeleton
            variant="rectangular"
            sx={{
                ...skeletonSx,
                width: '100%',
                height: 0,
                pt: '100%',
                borderRadius: '12px',
            }}
        />
        <Box>
            <Skeleton sx={skeletonSx} width="80%" height={48} />
            <Skeleton sx={skeletonSx} width="30%" height={40} />
            <Skeleton sx={{ ...skeletonSx, mt: 2 }} />
            <Skeleton sx={skeletonSx} />
            <Skeleton sx={skeletonSx} width="60%" />
        </Box>
    </Box>
);

const ProductDetail = ({
    storeId,
    productId,
    store,
    productState,
    cachedProduct,
    customer,
    isAdding,
    fetchStores,
    fetchStoreProduct,
    addToCart,
}) => {
    const navigate = useNavigate();
    const [selectedOptions, setSelectedOptions] = useState({});
    const [quantity, setQuantity] = useState(1);
    const [loginOpen, setLoginOpen] = useState(false);
    const [addedOpen, setAddedOpen] = useState(false);

    useEffect(() => {
        if (!store) {
            fetchStores();
        }
    }, [store, fetchStores]);

    useEffect(() => {
        fetchStoreProduct(storeId, productId);
    }, [storeId, productId, fetchStoreProduct]);

    const notFound = !!productState && productState.notFound;
    // mientras llega el detalle mostramos lo que ya vino en el listado
    const product = notFound
        ? null
        : (productState && productState.product) || cachedProduct;
    const failed = !product && !!productState && productState.failed;

    const storeLabel = store && store.name ? store.name : 'la tienda';
    const goToCall = () => navigate(callRequestPath(storeId));

    const productOptions = (product && product.options) || [];
    const missingOption = productOptions.find(
        (option) => !selectedOptions[option.name]
    );
    const canAdd = !!product && !missingOption && !isAdding;

    const selectOption = (name, value) =>
        setSelectedOptions((current) => ({ ...current, [name]: value }));

    const submitAdd = useCallback(async () => {
        const cart = await addToCart(storeId, {
            productId: Number(productId),
            quantity,
            selectedOptions,
        });
        if (cart) setAddedOpen(true);
    }, [addToCart, storeId, productId, quantity, selectedOptions]);

    // Sin sesión: el login del comprador en un diálogo; al volver completa
    // el agregado con lo que ya había elegido.
    const handleAdd = () => {
        if (!canAdd) return;
        if (!customer) {
            setLoginOpen(true);
            return;
        }
        submitAdd();
    };

    const handleLoggedIn = useCallback(() => {
        setLoginOpen(false);
        submitAdd();
    }, [submitAdd]);

    let content;
    if (notFound) {
        content = (
            <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_TITLE,
                        fontWeight: 800,
                        fontSize: { xs: '1.6rem', md: '2rem' },
                        textTransform: 'uppercase',
                        mb: 1,
                    }}
                >
                    Este producto ya no está disponible
                </Typography>
                <Typography
                    sx={{ color: COLORS.muted, fontFamily: FONT_BODY, mb: 3 }}
                >
                    Mirá el resto del catálogo o llamá a la tienda para
                    consultar.
                </Typography>
                <Button
                    variant="outlined"
                    component={RouterLink}
                    to={storePath(storeId)}
                    sx={{ ...secondaryButtonSx, px: 4 }}
                >
                    Ver la tienda
                </Button>
            </Box>
        );
    } else if (failed) {
        content = (
            <Box sx={{ textAlign: 'center', py: 10 }}>
                <Typography
                    sx={{ color: COLORS.muted, fontFamily: FONT_BODY, mb: 3 }}
                >
                    No pudimos cargar el producto. Igual podés consultar por
                    videollamada.
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={() => fetchStoreProduct(storeId, productId)}
                        sx={secondaryButtonSx}
                    >
                        Reintentar
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<VideoCallIcon />}
                        onClick={goToCall}
                        sx={callButtonSx}
                    >
                        Consultar por videollamada
                    </Button>
                </Box>
            </Box>
        );
    } else if (!product) {
        content = <DetailSkeleton />;
    } else {
        content = (
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: { xs: 3, md: 5 },
                    alignItems: 'start',
                }}
            >
                <Gallery
                    key={product.productId}
                    images={product.images}
                    name={product.name}
                />
                <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
                >
                    <Box>
                        <Typography
                            component="h1"
                            sx={{
                                fontFamily: FONT_TITLE,
                                fontWeight: 800,
                                fontSize: { xs: '1.8rem', md: '2.4rem' },
                                lineHeight: 1.05,
                                mb: 1,
                            }}
                        >
                            {product.name}
                        </Typography>
                        <PriceTag
                            price={product.price}
                            compareAtPrice={product.compareAtPrice}
                            size="large"
                        />
                    </Box>

                    <ProductOptions
                        options={product.options}
                        selected={selectedOptions}
                        onSelect={selectOption}
                        disabled={isAdding}
                    />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Typography
                            sx={{
                                color: COLORS.muted,
                                fontFamily: FONT_BODY,
                                fontSize: '0.9rem',
                            }}
                        >
                            Cantidad:
                        </Typography>
                        <QuantityStepper
                            value={quantity}
                            onChange={setQuantity}
                            disabled={isAdding}
                        />
                    </Box>

                    {product.description && (
                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.8)',
                                fontFamily: FONT_BODY,
                                fontSize: '0.95rem',
                                lineHeight: 1.7,
                                whiteSpace: 'pre-line',
                            }}
                        >
                            {product.description}
                        </Typography>
                    )}

                    <Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                gap: 1.5,
                                '& .MuiButton-root': { whiteSpace: 'nowrap' },
                            }}
                        >
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={
                                    isAdding ? (
                                        <CircularProgress
                                            size={18}
                                            sx={{ color: COLORS.background }}
                                        />
                                    ) : (
                                        <AddShoppingCartIcon />
                                    )
                                }
                                disabled={!canAdd}
                                onClick={handleAdd}
                                sx={{
                                    ...callButtonSx,
                                    '&.Mui-disabled': {
                                        backgroundColor:
                                            'rgba(255,255,255,0.12)',
                                        color: 'rgba(255,255,255,0.4)',
                                    },
                                }}
                            >
                                Agregar al carrito
                            </Button>
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={<VideoCallIcon />}
                                onClick={goToCall}
                                sx={secondaryButtonSx}
                            >
                                Consultar por videollamada
                            </Button>
                        </Box>
                        {missingOption && (
                            <Typography
                                sx={{
                                    color: COLORS.muted,
                                    fontFamily: FONT_BODY,
                                    fontSize: '0.85rem',
                                    mt: 1,
                                }}
                            >
                                Elegí {missingOption.name} para agregarlo al
                                carrito.
                            </Typography>
                        )}
                        <Typography
                            sx={{
                                color: COLORS.subtle,
                                fontFamily: FONT_BODY,
                                fontSize: '0.8rem',
                                mt: 1,
                            }}
                        >
                            ¿Dudas? Un asesor de {storeLabel} te muestra el
                            producto en vivo y cerrás la compra en la llamada.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                minHeight: '100vh',
                backgroundColor: COLORS.background,
                color: COLORS.text,
            }}
        >
            <link href={FONTS_HREF} rel="stylesheet" />
            <Box
                sx={{
                    maxWidth: 1100,
                    mx: 'auto',
                    px: { xs: 2, md: 4 },
                    py: { xs: 2, md: 4 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        mb: 3,
                    }}
                >
                    <Button
                        component={RouterLink}
                        to={storePath(storeId)}
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            color: COLORS.muted,
                            textTransform: 'none',
                            '&:hover': { color: 'white' },
                        }}
                    >
                        {store && store.name
                            ? `Volver a ${store.name}`
                            : 'Volver a la tienda'}
                    </Button>
                    <CartButton />
                </Box>
                {content}
            </Box>

            <CustomerLoginDialog
                open={loginOpen}
                onClose={() => setLoginOpen(false)}
                onLoggedIn={handleLoggedIn}
                title="Iniciá sesión para comprar"
                subtitle="Tu carrito queda guardado en tu cuenta. Usá Google o Apple, solo toma unos segundos."
            />

            <Snackbar
                open={addedOpen}
                autoHideDuration={5000}
                onClose={(event, reason) => {
                    if (reason !== 'clickaway') setAddedOpen(false);
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <SnackbarContent
                    message="Agregado al carrito"
                    sx={{
                        backgroundColor: COLORS.card,
                        color: 'white',
                        border: `1px solid ${COLORS.border}`,
                        fontFamily: FONT_BODY,
                    }}
                    action={
                        <Button
                            size="small"
                            onClick={() => navigate(cartPath(storeId))}
                            sx={{ color: COLORS.call, fontWeight: 700 }}
                        >
                            Ver carrito
                        </Button>
                    }
                />
            </Snackbar>
        </Box>
    );
};

// Producto ya visto en el catálogo de la tienda o en el feed del home.
const findCachedProduct = (catalog, storeId, productId) => {
    const sameId = (p) => String(p.productId) === String(productId);
    const storeCatalog = catalog.byStore[storeId];
    return (
        (storeCatalog && storeCatalog.items.find(sameId)) ||
        catalog.featured.items.find(sameId) ||
        null
    );
};

const mapStateToProps = (state, ownProps) => {
    return {
        store: state.stores.stores[ownProps.storeId],
        productState: state.catalog.products[ownProps.productId],
        customer: state.customer.customer,
        isAdding: state.cart.isAdding,
        cachedProduct: findCachedProduct(
            state.catalog,
            ownProps.storeId,
            ownProps.productId
        ),
    };
};

const ConnectedProductDetail = connect(mapStateToProps, {
    fetchStores,
    fetchStoreProduct,
    addToCart,
})(ProductDetail);

const ProductDetailWrapper = () => {
    const { storeId, productId } = useParams();
    // key: al navegar a otro producto se remonta (resetea la galería)
    return (
        <ConnectedProductDetail
            key={`${storeId}-${productId}`}
            storeId={storeId}
            productId={productId}
        />
    );
};

export default ProductDetailWrapper;
